import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import FeatureFlagRoute from 'App/components/FeatureFlagRoute/FeatureFlagRoute';
import { PERMISSION_TYPES } from 'App/utils/isPermitted';
import * as routeLookup from 'App/common/routeLookup';

import AdvancedReports from '@/app/components/AdvancedReports/AdvancedReports';
import AlertsCenter from 'App/components/AlertsCenter';
import AlertsManager from 'App/components/AlertsManager';
import MedicationManagement from 'App/components/MedicationManagement';
import ClinicalSummary from '@/app/features/ClinicalSummary';
import EditPatientBranch from '@/app/components/EditPatientBranch/EditPatientBranch';
import PatientUpload from '@/app/components/Upload/PatientUpload';
import PhysicianUpload from '@/app/components/Upload/PhysicianUpload';
import ProtocolManager from 'App/features/ProtocolManager/ProtocolManagerPage';
import QuestionGroups from 'App/components/QuestionGroups';
import TherapeuticCategories from 'App/components/TherapeuticCategories';
import ViewMessages from '@/app/components/ViewMessages/ViewMessages';
import ProtocolPublisher from 'App/components/ProtocolPublisher';
import ProtocolTemplateBuilderPage from 'App/features/ProtocolTemplateBuilder';
import SsoConfiguration from 'App/components/SsoConfiguration';
import FindPatient from '@/app/components/FindPatient/FindPatient';
import PatientProfile from '@/app/components/PatientProfile';
import PatientMessaging from 'App/components/PatientMessaging';
import ContentMonographDisplay from 'App/features/ContentMonograph/ContentMonographDisplay';
import Activity from 'App/features/Activity/Activity';
import AssessmentPreview from 'App/features/AssessmentPreview/AssessmentPreview';
import WorkQueue from 'App/features/WorkQueue/WorkQueue';
import AddPatient from 'App/features/AddPatient/AddPatient';
import EditPatient from 'App/features/EditPatient/EditPatient';
import QueueManager from 'App/features/QueueManager/QueueManager';
import UserManager from 'App/features/UserManager';
import AssignmentManagerPage from 'App/features/AssignmentManager/AssignmentManagerPage';
import EditUser from 'App/features/UserManager/EditUser';
import CreateUser from 'App/features/UserManager/CreateUser';
import ViewDisabledUser from 'App/features/UserManager/ViewDisabledUser';
import AssessmentManagerRouter from 'App/features/AssessmentManager/AssessmentManagerRouter';
import TherigyInsights from 'App/features/TherigyInsights';
import MyAccount from 'App/features/MyAccount';
import AccessViewPage from 'App/features/AccessView/AccessViewPage';
import FindPatientPdxPage from 'App/features/FindPatientPdx';
import AssessmentPublisherPage from 'App/features/AssessmentPublisher/AssessmentPublisherPage';
import ContentLibraryPage from 'App/features/ContentLibrary/ContentLibraryHome/ContentLibraryPage';
import ContentLibraryEditPage from 'App/features/ContentLibrary/ContentLibraryEdit/ContentLibraryEditPage';
import ContentPublisherPage from 'App/features/ContentPublisher/ContentPublisherPage';
import ReferralManagerHome from 'App/features/ReferralManager/ReferralManagerHome';
import ReferralDetail from 'App/features/ReferralManager/Referral/ReferralDetail';
import PriorAuthorization from 'App/features/ReferralManager/Referral/PriorAuthorization';
import AppealPage from 'App/features/ReferralManager/Referral/AppealPage';
import FinancialAssistance from 'App/features/ReferralManager/Referral/FinancialAssistance';
import BenefitsInvestigation from 'App/features/ReferralManager/Referral/BenefitsInvestigation';
import AddMedication from 'App/features/AddEditMedication/AddMedication';
import EditMedication from 'App/features/AddEditMedication/EditMedication';
import UnassignedPatient from '../UnassignedPatient/UnassignedPatient';

function AuthenticatedRoutes() {
    return (
        <Routes>
            {/* Header routes */}
            <Route path={routeLookup.accessViewRoute} element={<AccessViewPage />} />
            <Route path={routeLookup.myAccountRoute} element={<MyAccount />} />
            <Route path={`${routeLookup.alertsCenterRoute}/*`} element={<AlertsCenter />} />
            <Route path={routeLookup.viewMessagesRoute} element={<ViewMessages />} />

            {/* Work Queue routes */}
            <Route path={routeLookup.workQueueRoute} element={<WorkQueue />} />
            <Route path={routeLookup.unassignedPatientRoute} element={<UnassignedPatient />} />

            {/* Patient routes */}
            <Route path={routeLookup.patientRoute} element={<PatientProfile />} />
            <Route path={routeLookup.addPatientRoute} element={<AddPatient />} />
            <Route path={routeLookup.editPatientRoute} element={<EditPatient />} />
            <Route path={routeLookup.clinicalSummaryRoute} element={<ClinicalSummary />} />
            <Route path={routeLookup.editPatientBranchRoute} element={<EditPatientBranch />} />

            {/* Assessment routes */}
            <Route path={routeLookup.activityRoute} element={<Activity />} />
            <Route path={routeLookup.assessmentPreviewRoute} element={<AssessmentPreview />} />
            <Route path={routeLookup.contentMonographRoute} element={<ContentMonographDisplay />} />

            {/* Referral routes */}
            <Route path={routeLookup.referralManagerRoute} element={<ReferralManagerHome />} />
            <Route path={routeLookup.referralDetailRoute} element={<ReferralDetail />} />
            <Route path={routeLookup.referralBenefitsInvestigationRoute} element={<BenefitsInvestigation />} />
            <Route path={routeLookup.referralPriorAuthorizationRoute} element={<PriorAuthorization />} />
            <Route path={routeLookup.referralFinancialAssistanceRoute} element={<FinancialAssistance />} />
            <Route path={routeLookup.referralAppealRoute} element={<AppealPage />} />

            {/* Find Patient routes */}
            <Route path={routeLookup.findPatientRoute} element={<FindPatient />} />
            <Route path={routeLookup.pdxFindPatientRoute} element={<FindPatientPdxPage />} />

            {/* Reporting routes */}
            <Route path={routeLookup.advancedReportsRoute} element={<AdvancedReports />} />
            <Route path={routeLookup.therigyInsightsRoute} element={<TherigyInsights />} />

            {/* Tools routes */}
            <Route
                path={routeLookup.addMedicationRoute}
                element={
                    <FeatureFlagRoute featureFlagName={PERMISSION_TYPES.ENABLE_CODIFY_MEDICATIONS}>
                        <AddMedication />
                    </FeatureFlagRoute>
                }
            />
            <Route
                path={routeLookup.editMedicationRoute}
                element={
                    <FeatureFlagRoute featureFlagName={PERMISSION_TYPES.ENABLE_CODIFY_MEDICATIONS}>
                        <EditMedication />
                    </FeatureFlagRoute>
                }
            />

            <Route path={routeLookup.alertsManagerRoute} element={<AlertsManager />} />

            {AssessmentManagerRouter}

            <Route path={routeLookup.assessmentPublisherRoute} element={<AssessmentPublisherPage />} />

            <Route path={routeLookup.contentLibraryRoute} element={<ContentLibraryPage />} />
            <Route path={routeLookup.contentLibraryEditRoute} element={<ContentLibraryEditPage />} />

            <Route path={routeLookup.contentPublisherRoute} element={<ContentPublisherPage />} />

            <Route path={`${routeLookup.therapeuticCategoriesRoute}/*`} element={<TherapeuticCategories />} />

            <Route path={routeLookup.questionGroupsRoute} element={<QuestionGroups />} />

            <Route path={routeLookup.assignmentManagerRoute} element={<AssignmentManagerPage />} />

            <Route path={routeLookup.medicationManagementRoute} element={<MedicationManagement />} />

            <Route path={routeLookup.patientMessagingRoute} element={<PatientMessaging />} />

            <Route path={`${routeLookup.protocolManagerRoute}/*`} element={<ProtocolManager />} />

            <Route path={routeLookup.protocolPublisherRoute} element={<ProtocolPublisher />} />

            <Route path={`${routeLookup.protocolTemplateBuilderRoute}/*`} element={<ProtocolTemplateBuilderPage />} />

            <Route path={routeLookup.queueManagerRoute} element={<QueueManager />} />

            <Route path={routeLookup.userManagerRoute} element={<UserManager />} />
            <Route path={routeLookup.createUserRoute} element={<CreateUser />} />
            <Route path={routeLookup.enableUserRoute} element={<ViewDisabledUser isEnabling />} />
            <Route path={routeLookup.viewDisabledUserRoute} element={<ViewDisabledUser />} />
            <Route path={routeLookup.viewEditUserRoute} element={<EditUser />} />

            <Route path={routeLookup.patientUploadRoute} element={<PatientUpload />} />
            <Route path={routeLookup.physicianUploadRoute} element={<PhysicianUpload />} />

            <Route path={routeLookup.ssoConfigurationRoute} element={<SsoConfiguration />} />

            {/* Default redirect */}
            <Route index element={<Navigate replace to={routeLookup.workQueueRoute} />} />
        </Routes>
    );
}

export default AuthenticatedRoutes;
